<template>
    <div class="bg-white border border-border rounded-md lg:mb-5 mb-4 lg:hidden block" v-if="state.disp_enroll_section && !state.disp_err_msg && state.bundled_group.filter(g => g.select_plan).length > 0">
        <div v-for="group in state.bundled_group.filter(group => group.select_plan)" :key="group.id">
            <h2 class="card-title p-4 border-b border-border !m-0">Selected Plan Information</h2>
            <p class="text-red-500 px-5 py-2" v-if="state.duplicate_value">
  You have selected duplicate plans. Please correct this mistake.
</p>
            <div class="p-4">
                <div class="mb-4">
                    <div class="flex justify-between mb-2.5">
                        <p class="content !mb-0">Plan Name</p>
                        <span class="content !text-blackPrimary !mb-0 basis-[50%] text-end lg:basis-[50%] !uppercase">{{
                            group.description }}</span>
                    </div>

                    <div class="flex justify-between mb-2.5">
                        <p class="content !mb-0">Company Name</p>
                        <span class="content !text-blackPrimary !mb-0 basis-[50%] text-end lg:basis-[50%]">{{
                            state.company?.company_name }}</span>
                    </div>

                    <div class="flex justify-between mb-2.5">
                        <p class="content !mb-0">State</p>
                        <span class="content !text-blackPrimary !mb-0 basis-[50%] text-end lg:basis-[50%] uppercase">{{ group.state }}</span>
                    </div>

                    <div class="flex justify-between mb-2.5">
                        <p class="content !mb-0">Effective Date</p>
                        <span class="content !text-blackPrimary !mb-0 !font-bold basis-[50%] text-end lg:basis-[50%]">{{
                            formatUsDate(state.eff_date) }}</span>
                    </div>
                </div>
                <div class="mb-4 p-[15px] border border-border rounded-md bg-primaryLight">
                    <div class="flex justify-between mb-2.5">
                        <p class="content !mb-0" v-if="state.payroll_enrollment !== '1'">
                            <template v-if="isStudentFlow()">{{ group.student_premium_label }}</template>
                            <template v-else>
                                <span v-if="state.payment_frequency === 1">Annual</span>
                                <span v-else-if="state.payment_frequency === 2">Semi-Annual</span>
                                <span v-else-if="state.payment_frequency === 4">Per Quarter</span>
                                <span v-else-if="state.payment_frequency === 9">9 Pay Periods</span>
                                <span v-else-if="state.payment_frequency === 12">Per Month</span>
                                <span v-else-if="state.payment_frequency === 20">20 Pay Periods</span>
                                <span v-else-if="state.payment_frequency === 22">22 Pay Periods</span>
                                <span v-else-if="state.payment_frequency === 24">Semi-Monthly</span>
                                <span v-else-if="state.payment_frequency === 26">Bi-Weekly</span>
                                <span v-else-if="state.payment_frequency === 52">Per Week</span>
                            </template>
                        </p>
                        <p class="content !mb-0" v-if="state.payroll_enrollment == '1'">
                            Premium
                        </p>
                        <span class="content !text-blackPrimary !mb-0 !font-bold">
                            ${{ studentPremiumAmount(group) }}
                        </span>
                    </div>

                    <div
                        class="flex justify-between mb-2.5"
                        v-if="isStudentFlow() && state.student_billing_period === 'annual' && group.student_show_discount"
                    >
                        <p class="content !mb-0 student-discount-label">
                            Discount Amount<span v-if="group.student_discount_percent"> ({{ formatDiscountPercent(group) }})</span>
                        </p>
                        <span class="content !mb-0 !font-bold student-discount-value">-${{ group.student_discount_amount }}</span>
                    </div>

                    <div class="flex justify-between mb-2.5" v-if="state.payment_method == 'CC' && !isStudentFlow()">
                        <p class="content !mb-0">Prorated Premium</p>
                        <span class="content !text-blackPrimary !mb-0 !font-bold">${{ group.prorated_premium }}</span>
                    </div>

                    <div class="flex justify-between mb-2.5" v-if="state.payment_method == 'CC' && planServiceFee(group) > 0">
                        <p class="content !mb-0">Service Fee</p>
                        <span class="content !text-blackPrimary !mb-0 !font-bold">${{ planServiceFee(group).toFixed(2) }}</span>
                    </div>

                    <div class="flex justify-between mb-0">
                        <p class="content !font-bold !mb-0 !text-blackPrimary">Final Amount</p>
                        <span v-if="state.payment_method != 'Payroll'"
                            class="content !mb-0 !font-bold !text-lg !text-primary">${{ group.total }}</span>
                            <span v-if="state.payment_method == 'Payroll'"
                            class="content !mb-0 !font-bold !text-lg !text-primary">${{ group.total_monthly_prem }}</span>
                    </div>
                </div>
                <div v-if="state.payment_method == 'CC' && isStudentFlow()">
                    <p class="content" v-if="isStudentFlow() && state.student_billing_period === 'annual'">
                        Your credit/debit card will be charged a yearly premium corresponding with your effective date
                        {{ formatUsDate(state.eff_date) }}. Your plan will automatically renew each year and the recurring yearly premium will be processed on
                        {{ formatUsDate(state.next_recurring_date || state.eff_date) }} and every year thereafter, until canceled.
                    </p>
                    <p class="content" v-else>
                        Your credit/debit card will be charged a monthly premium corresponding with your effective date
                        {{ formatUsDate(state.eff_date) }}. Your plan will automatically renew each month and the recurring monthly premium will be processed on
                        {{ formatUsDate(state.next_recurring_date || state.eff_date) }} and every month thereafter, until canceled.
                    </p>
                </div>
                <div v-else-if="state.payment_method == 'CC'">
                    <p class="content">
                        Your credit card will be charged premium corresponding with your effective date
                        {{ formatUsDate(state.eff_date) }} and includes next month's monthly premium. Thereafter, your monthly
                        premium
                        will be processed on the 20th on a recurring monthly cycle beginning {{ formatNextRecurringDate() }}.
                    </p>
                    <p class="content !mb-0" v-if="planServiceFee(group) > 0">The prorated premium due for the remainder of this month, based
                        on
                        your
                        effective date will be ${{ group.total_prorated_prem }}. Your monthly premium will be
                        ${{ group.total_monthly_prem }} with a ${{ planServiceFee(group).toFixed(2) }}
                        monthly
                        transaction service fee, for a total of ${{ calculateTotal(group.total_monthly_prem, planServiceFee(group)) }} a month.</p>
                    <p class="content !mb-0" v-else>The prorated premium due for the remainder of this month, based
                        on
                        your
                        effective date will be ${{ group.total_prorated_prem }}. Your monthly premium will be
                        ${{ group.total_monthly_prem }} for a total of ${{ group.total_monthly_prem }} a month.</p>
                </div>
                <div v-if="state.payment_method == 'Payroll'">
                    <div class="content">
                        Effective date : {{ formatUsDate(state.eff_date) }}

                        <p>Your payroll/settlement deduction will be ${{ group.total_monthly_prem }}</p>
                        <p>Your payroll/settlement deduction is based on the deduction frequency of your company.</p>
                        <p>Payroll/settlement deduction authorization will be submitted to
                            {{ state.company.company_name }}.</p>
                    </div>

                </div>
            </div>
        </div>
    </div>
</template>
<script>
import {
  isStudentFlow as checkStudentFlow,
  resolveServiceFeeAmount,
} from "@/utils/enrollmentFlow";

export default {
    name: "SelectedPlanInfoMob",
    props: {
        state: Object
    },
    methods: {
        isStudentFlow() {
            return checkStudentFlow(this.state);
        },
        formatUsDate(value) {
            if (!value) return "";
            return new Date(value).toLocaleDateString("en-US", {
                month: "2-digit",
                day: "2-digit",
                year: "numeric",
            });
        },
        formatNextRecurringDate() {
            const value = this.state.next_recurring_date || this.state.next_month;
            if (!value) return "";
            return this.formatUsDate(value);
        },
        recurringDayOfMonth(value) {
            const day = new Date(value).getDate();
            const suffix =
                day % 10 === 1 && day !== 11
                    ? "st"
                    : day % 10 === 2 && day !== 12
                    ? "nd"
                    : day % 10 === 3 && day !== 13
                    ? "rd"
                    : "th";
            return `${day}${suffix}`;
        },
        formatDiscountPercent(group) {
            const percent = Number(group?.student_discount_percent);
            if (!Number.isFinite(percent) || percent <= 0) return "";
            const formatted = Number.isInteger(percent) ? percent : percent.toFixed(1);
            return `${formatted}%`;
        },
        studentPremiumAmount(group) {
            if (!this.isStudentFlow()) {
                return group.monthly_premium;
            }
            if (this.state.student_billing_period === "annual") {
                return group.student_display_rate || group.student_yearly_premium_gross;
            }
            return group.student_display_rate || group.monthly_premium;
        },
        planServiceFee(group) {
            return resolveServiceFeeAmount(this.state, group);
        },
        calculateTotal(total, fee) {
            return (parseFloat(total) + parseFloat(fee)).toFixed(2);
        },
    },
};
</script>
